import { creativeFormatForSize } from '@digital-twins/shared-types'
import { AssistantImageProgressEvent, ThreadAssetView } from '@digital-twins/shared-types/assistant'

/**
 * Live state of one `generate_image` call, folded from its `image_progress`
 * frames (image plan, lifecycle table). Never persisted: a reload shows the
 * committed asset through the Assets rail and the answer's `asset:` ref.
 */
export type GeneratingImageStage = AssistantImageProgressEvent['stage']

export type GeneratingImageSize = NonNullable<AssistantImageProgressEvent['size']>

export interface GeneratingImageState {
  callId: string
  stage: GeneratingImageStage
  size?: GeneratingImageSize
  previewDataUrl?: string
  asset?: ThreadAssetView
  reason?: string
}

/** Keyed by provider call id; object key order is arrival order. */
export type ImageProgressByCallId = Record<string, GeneratingImageState>

const TERMINAL_STAGES: ReadonlySet<GeneratingImageStage> = new Set(['done', 'failed', 'stopped'])

export function isTerminalImageStage(stage: GeneratingImageStage): boolean {
  return TERMINAL_STAGES.has(stage)
}

/**
 * Folds one frame into the per-call map. A call that already ended ignores
 * later frames, so a late partial can never blur a finished image.
 */
export function applyImageProgressFrame(
  current: ImageProgressByCallId | undefined,
  frame: AssistantImageProgressEvent,
): ImageProgressByCallId {
  const map = current ?? {}
  const previous = map[frame.callId]
  if (previous && isTerminalImageStage(previous.stage)) {
    return map
  }
  const next: GeneratingImageState = {
    callId: frame.callId,
    stage: frame.stage,
    size: frame.size ?? previous?.size,
    previewDataUrl: frame.previewDataUrl ?? previous?.previewDataUrl,
    asset: frame.asset ?? previous?.asset,
    reason: frame.reason ?? previous?.reason,
  }
  return { ...map, [frame.callId]: next }
}

/** Stop or abort: every call still running reads as stopped. */
export function stopPendingImages(
  current: ImageProgressByCallId | undefined,
): ImageProgressByCallId | undefined {
  if (!current) {
    return current
  }
  let changed = false
  const next: ImageProgressByCallId = {}
  for (const [callId, state] of Object.entries(current)) {
    if (isTerminalImageStage(state.stage)) {
      next[callId] = state
    } else {
      next[callId] = { ...state, stage: 'stopped' }
      changed = true
    }
  }
  return changed ? next : current
}

/**
 * Tiles still worth showing beside the answer: every call, except a finished
 * one whose image the answer on screen already renders inline (no duplicate).
 * `placedAssetTokens` are the complete, resolved `asset:` image refs the
 * answer body reports, so a ref still being written keeps its tile.
 */
export function visibleImageTiles(
  current: ImageProgressByCallId | undefined,
  placedAssetTokens: ReadonlyArray<string>,
): Array<GeneratingImageState> {
  if (!current) {
    return []
  }
  return Object.values(current).filter((state) => {
    if (state.stage !== 'done' || !state.asset?.token) {
      return true
    }
    return !placedAssetTokens.includes(state.asset.token)
  })
}

/** Width over height for the reserved box; square until the size is known. */
export function imageAspectRatio(size: GeneratingImageSize | undefined): string {
  return (creativeFormatForSize(size)?.ratio ?? '1:1').replace(':', ' / ')
}

/**
 * The widest a tile can be so its height stays within the inline image's cap
 * (`max-h-80 sm:max-h-96`), else a 9:16 tile grows tall and jumps when the answer's
 * image takes over. `--tile-max-h` is set per breakpoint on the tile.
 */
export function imageTileMaxWidth(size: GeneratingImageSize | undefined): string {
  const [width, height] = (creativeFormatForSize(size)?.ratio ?? '1:1').split(':').map(Number)
  return `min(24rem, calc(var(--tile-max-h) * ${width / height}))`
}
